/**
 * The stand's settings — graph, model(s), theme — moved out of the header's ⚙︎
 * menu into the «Отладка» drawer (owner, 07.10.2026: the chat screens of the
 * Figma page have no settings at all, and prod need not mirror dev). The groups
 * and their rules are the menu's, unchanged:
 *
 * **Checked = what is ACTUALLY running** (owner, 2026-09-16) — the pin, else
 * `run_meta`, else the deploy's own default — never a placeholder row
 * (`sessionStore.orchChoiceIdFor` / `modelChoiceIdFor` / `workerChoiceIdFor`).
 *
 * **Tapping the row that equals the default CLEARS the override** rather than
 * pinning it. Which row is the default is learned from whatever is checked
 * while NO override is active; while one IS active neither source can say, so
 * `defaults` below remembers it across that stretch.
 *
 * The line the header's subtitle used to carry — the variant in words, the
 * model that ran, the helper, the graph id — is the first thing here.
 */

import { useEffect, type ReactNode } from 'react';
import {
  GRAPH_CHOICES,
  deployDefaultChoiceId,
  GRAPH_SWITCH,
  graphChoiceFor,
  hasWorkerModelSwitch,
  helperChoicesFor,
  modelChoiceIdFor,
  modelChoicesFor,
  NO_OVERRIDE,
  orchChoiceIdFor,
  ORCH_MODEL_CHOICES,
  switchGraph,
  useSession,
  usesOrchModelMenu,
  workerChoiceIdFor,
  workerModelInUseFor,
  type ThemeChoice,
} from '../store/sessionStore';
import { useChat } from '../store/chatStore';
import { cn } from '../lib/cn';

/** `openai/GigaChat-2-Max` → `GigaChat-2-Max`: the provider prefix is routing. */
function shortModel(name: string): string {
  const cut = name.lastIndexOf('/');
  return cut === -1 ? name : name.slice(cut + 1);
}

const THEMES: { id: ThemeChoice; label: string }[] = [
  { id: 'system', label: 'Как в системе' },
  { id: 'light', label: 'Светлая' },
  { id: 'dark', label: 'Тёмная' },
];

/**
 * Which row is each group's DEFAULT, learned while no override is active — kept
 * for the PAGE's lifetime, not the panel's: the drawer unmounts this component
 * on every close, and a default forgotten there turns the next tap on that row
 * into a pin instead of a clear (the ⚙︎ menu's refs lived as long as the header
 * did). A graph switch reloads the page, so these never cross graphs.
 */
const defaults: { orch?: string; model?: string; worker?: string } = {};

function Choice({
  checked,
  onClick,
  children,
}: {
  checked: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" role="radio" aria-checked={checked} className="menu__item" onClick={onClick}>
      <span className="menu__check">{checked ? '✓' : ''}</span>
      {children}
    </button>
  );
}

export default function SettingsPanel() {
  const modelChoice = useSession((state) => state.modelChoice);
  const setModelChoice = useSession((state) => state.setModelChoice);
  const orchModelChoice = useSession((state) => state.orchModelChoice);
  const setOrchModelChoice = useSession((state) => state.setOrchModelChoice);
  const workerModelChoice = useSession((state) => state.workerModelChoice);
  const setWorkerModelChoice = useSession((state) => state.setWorkerModelChoice);
  useSession((state) => state.deployModels);
  const theme = useSession((state) => state.theme);
  const setTheme = useSession((state) => state.setTheme);
  const assistantId = useSession((state) => state.assistantId);
  const modelInUse = useChat((state) => state.modelInUse);
  const workerModelInUse = useChat((state) => state.workerModelInUse);

  const modelChoices = modelChoicesFor(assistantId);
  const requested = modelChoices.find((choice) => choice.id === modelChoice);
  const modelLine = modelInUse
    ? modelInUse
    : requested?.model
      ? `${requested.label} · запрошено`
      : 'модель станет известна после первого ответа';
  const variant = graphChoiceFor(assistantId);

  const orchOverride = usesOrchModelMenu(assistantId)
    ? ORCH_MODEL_CHOICES.find((choice) => choice.id === orchModelChoice)
    : undefined;

  const helperChoices = helperChoicesFor(assistantId);
  const showsWorkerModel = hasWorkerModelSwitch(assistantId) || usesOrchModelMenu(assistantId);
  const namesSecondContourOnly = usesOrchModelMenu(assistantId) && !hasWorkerModelSwitch(assistantId);
  const secondContour = showsWorkerModel ? workerModelInUseFor(workerModelInUse) : undefined;

  const checkedOrchId = orchChoiceIdFor(modelInUse);
  const checkedModelId = usesOrchModelMenu(assistantId) ? undefined : modelChoiceIdFor(modelChoices, modelInUse);
  const checkedWorkerId = workerChoiceIdFor(workerModelInUse);
  // The deploy file answers too: a page opened with a pin never learns `defaults.model`.
  const defaultModelId = defaults.model ?? deployDefaultChoiceId(modelChoices);

  useEffect(() => {
    if (orchModelChoice === NO_OVERRIDE && checkedOrchId) defaults.orch = checkedOrchId;
  }, [orchModelChoice, checkedOrchId]);
  useEffect(() => {
    if (modelChoice === NO_OVERRIDE && checkedModelId) defaults.model = checkedModelId;
  }, [modelChoice, checkedModelId]);
  useEffect(() => {
    if (workerModelChoice === NO_OVERRIDE && checkedWorkerId) defaults.worker = checkedWorkerId;
  }, [workerModelChoice, checkedWorkerId]);

  return (
    <section className="dev-settings" aria-label="Настройки">
      <h2>Настройки</h2>
      <p className="dev-muted">
        {variant ? `${variant.label} · ${variant.description}` : assistantId}
        <br />
        Модель: {modelLine}
        {secondContour ? ` · второй контур: ${shortModel(secondContour)}` : ''}
        {orchOverride?.model ? ` · оркестратор запрошен на: ${orchOverride.label}` : ''}
        <br />
        Граф: {assistantId}
      </p>

      {GRAPH_SWITCH ? (
        <div className="menu__group" role="radiogroup" aria-label="Граф">
          <div className="menu__label">Граф</div>
          {GRAPH_CHOICES.map((choice) => (
            <Choice
              key={choice.id}
              checked={choice.id === assistantId}
              onClick={() => {
                if (choice.id !== assistantId) switchGraph(choice.id);
              }}
            >
              {choice.label}
            </Choice>
          ))}
        </div>
      ) : null}

      {GRAPH_SWITCH && usesOrchModelMenu(assistantId) ? (
        <>
          <div className="menu__group" role="radiogroup" aria-label="Первый контур">
            <div className="menu__label">Первый контур</div>
            {ORCH_MODEL_CHOICES.map((choice) => (
              <Choice
                key={choice.id}
                checked={choice.id === checkedOrchId}
                onClick={() => setOrchModelChoice(choice.id === defaults.orch ? NO_OVERRIDE : choice.id)}
              >
                {choice.label}
              </Choice>
            ))}
          </div>

          {namesSecondContourOnly ? (
            <div className="menu__group">
              <div className="menu__label">Второй контур</div>
              <div
                aria-disabled="true"
                className={cn('menu__item', 'menu__item--static')}
                title="Модель помощника задаёт деплой; на ход не переключается"
              >
                <span className="menu__check">{secondContour ? '✓' : ''}</span>
                {secondContour ? shortModel(secondContour) : 'станет известна после первого хода'}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="menu__group" role="radiogroup" aria-label="Модель">
          <div className="menu__label">{hasWorkerModelSwitch(assistantId) ? 'Первый контур' : 'Модель'}</div>
          {modelChoices.map((choice) => (
            <Choice
              key={choice.id}
              checked={choice.id === checkedModelId}
              onClick={() => setModelChoice(choice.id === defaultModelId ? NO_OVERRIDE : choice.id)}
            >
              {choice.label}
            </Choice>
          ))}
        </div>
      )}

      {GRAPH_SWITCH && hasWorkerModelSwitch(assistantId) ? (
        <div className="menu__group" role="radiogroup" aria-label="Второй контур">
          <div className="menu__label">
            Второй контур
            {secondContour && !checkedWorkerId ? ` · ${shortModel(secondContour)}` : ''}
          </div>
          {helperChoices.map((choice) => (
            <Choice
              key={choice.id}
              checked={choice.id === checkedWorkerId}
              onClick={() => setWorkerModelChoice(choice.id === defaults.worker ? NO_OVERRIDE : choice.id)}
            >
              {choice.label}
            </Choice>
          ))}
        </div>
      ) : null}

      <div className="menu__group" role="radiogroup" aria-label="Тема">
        <div className="menu__label">Тема</div>
        {THEMES.map((option) => (
          <Choice key={option.id} checked={option.id === theme} onClick={() => setTheme(option.id)}>
            {option.label}
          </Choice>
        ))}
      </div>
    </section>
  );
}
